import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  Alert,
  Card,
  Col,
  Container,
  Row,
  Spinner,
} from 'react-bootstrap';
import { HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import { fetchProfileLookups, hydrateProfile } from '../lib/profileLookups';

const faqs = [
  {
    question: 'Kenapa data yang saya simpan tidak muncul?',
    answer: 'Pastikan data berhasil disimpan dan halaman sudah diperbarui.',
  },
  {
    question: 'Apa perbedaan Arsip Data dan Laporan?',
    answer:
      'Arsip Data menampilkan data yang tersimpan, sedangkan Laporan menampilkan rekap kesiapan akreditasi.',
  },
  {
    question:
      'Kenapa saya tidak bisa mengubah role atau prodi di Akun Saya?',
    answer:
      'Informasi tersebut dikunci untuk menjaga keamanan sistem dan hanya dapat diubah oleh superadmin.',
  },
  {
    question: 'Apakah data yang dihapus hilang permanen?',
    answer:
      'Tidak. Sistem menggunakan soft delete, sehingga data yang dihapus tidak langsung hilang permanen.',
  },
  {
    question: 'Bagaimana cara mengganti password?',
    answer:
      'Gunakan menu Pengaturan > Akun Saya, lalu isi password baru dan konfirmasi password.',
  },
  {
    question: 'Apa yang harus dilakukan jika ada kesalahan data?',
    answer:
      'Gunakan tombol edit pada data yang sudah tersimpan atau hubungi admin sistem jika diperlukan.',
  },
];

function BantuanPage() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    const { data: profileData, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!profileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    try {
      const lookups = await fetchProfileLookups(supabase);
      setProfile(hydrateProfile(profileData, lookups));
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (loading) {
    return (
      <DashboardLayout
        title="Bantuan"
        breadcrumbs={['Dashboard', 'Bantuan']}
        profile={profile}
        activeMenu="bantuan"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Bantuan"
      breadcrumbs={['Dashboard', 'Bantuan']}
      profile={profile}
      activeMenu="bantuan"
    >
      <Container fluid className="p-0">
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        <Card className="help-hero-card mb-3">
          <Card.Body>
            <div className="help-hero-icon">
              <HelpCircle size={30} />
            </div>
            <div>
              <div className="hero-eyebrow-v2">Bantuan</div>
              <h2 className="help-hero-title">Bantuan Sistem</h2>
            </div>
          </Card.Body>
        </Card>

        <Row className="g-3">
          <Col xl={8}>
            <Card className="help-card">
              <Card.Body>
                <h4 className="section-title-v2 mb-2">Pertanyaan Umum</h4>
                <Accordion className="help-faq" defaultActiveKey="0">
                  {faqs.map((item, index) => (
                    <Accordion.Item eventKey={String(index)} key={item.question}>
                      <Accordion.Header>{item.question}</Accordion.Header>
                      <Accordion.Body>{item.answer}</Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="help-card help-contact-card">
              <Card.Body>
                <h4 className="section-title-v2 mb-2">Kontak Admin</h4>
                <div className="help-contact-box">
                  <div className="info-label-v2">Email Admin</div>
                  <a
                    className="info-value-v2 d-inline-block"
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=hokkingtular69@gmail.com"
                    target="_blank"
                    rel="noreferrer"
                  >
                    hokkingtular69@gmail.com
                  </a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </DashboardLayout>
  );
}

export default BantuanPage;
